import {
  ChangeDetectorRef,
  Component,
  inject,
  Inject,
  OnInit,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MaterialModule } from '../../../shared/material/material.module';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom, Subscription } from 'rxjs';
import { FormBuilder, Validators, FormsModule } from '@angular/forms';
import { MatTable } from '@angular/material/table';
import {
  ProductPackage,
  ProductPackageControllerService,
  ProductPackageItem,
  Product,
  ProductControllerService,
  ProductPackageFixedAmountDiscount,
  ProductPackagePercentageDiscount,
} from '../../../core/generated';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PackageDetailsDialogComponent } from './package-details-dialog/package-details-dialog.component';

@Component({
  selector: 'app-admin-product-package-dialog',
  standalone: true,
  imports: [MaterialModule],
  template: `
    <div class="modal-backdrop">
      <div class="modal-container">
        <div class="modal">
          <div class="modal-header">
            <h3 class="modal-title">{{ data.package ? 'Editar Pacote' : 'Novo Pacote' }}</h3>
          </div>

          <mat-dialog-content class="modal-body">
            <form [formGroup]="packageForm">
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>Nome do Pacote</mat-label>
                <input matInput formControlName="name" placeholder="Ex: Pacote Starter">
                <mat-error *ngIf="packageForm.get('name')?.hasError('required')">Campo obrigatório</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>Descrição</mat-label>
                <textarea matInput formControlName="description" rows="3" placeholder="Descrição curta do pacote"></textarea>
                <mat-error *ngIf="packageForm.get('description')?.hasError('required')">Campo obrigatório</mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>Tipo de Pacote</mat-label>
                <mat-select formControlName="packageType">
                  @for(packageType of packageTypeOptions; track packageType) {
                  <mat-option [value]="packageType">{{ packageType }}</mat-option>
                  }
                </mat-select>
                <mat-error *ngIf="packageForm.get('packageType')?.hasError('required')">Campo obrigatório</mat-error>
              </mat-form-field>

              <mat-checkbox formControlName="active">Pacote Ativo</mat-checkbox>
            </form>
          </mat-dialog-content>

          <mat-dialog-actions class="modal-footer">
            <button mat-button class="btn btn-ghost" (click)="onCancel()">
              <mat-icon>close</mat-icon> Cancelar
            </button>
            <button mat-flat-button color="primary" class="btn btn-primary" (click)="onSave()" [disabled]="packageForm.invalid">
              <mat-icon>save</mat-icon> Salvar
            </button>
          </mat-dialog-actions>
        </div>
      </div>
    </div>
  `,
  styles: [`
    mat-form-field { 
      width: 100%; 
      margin-bottom: var(--spacing-sm); 
    }
    mat-checkbox {
      margin-bottom: var(--spacing-md);
    }
  `]
})
export class AdminProductPackageDialogComponent {
  fb = inject(FormBuilder);
  dialogRef = inject(MatDialogRef<AdminProductPackageDialogComponent>);

  packageTypeOptions: string[] = [];

  packageForm = this.fb.group({
    id: [null as number | null],
    name: ['', Validators.required],
    description: ['', Validators.required],
    packageType: ['', Validators.required],
    active: [true],
  });

  constructor(@Inject(MAT_DIALOG_DATA) public data: { package?: ProductPackage }) {
    this.loadPackageTypes();
    
    if (this.data.package) {
      this.packageForm.patchValue({
        id: this.data.package.id ?? null,
        name: this.data.package.name ?? '',
        description: this.data.package.description ?? '',
        packageType: this.data.package.packageType ?? '',
        active: this.data.package.active ?? true,
      });
    }
  }

  async loadPackageTypes() {
    try {
      const service = inject(ProductPackageControllerService);
      this.packageTypeOptions = await firstValueFrom(service.getPackageTypes());
    } catch (error) {
      console.error('Erro ao carregar tipos de pacote:', error);
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    if (this.packageForm.valid) {
      const packageData: ProductPackage = {
        id: this.packageForm.value.id ?? undefined,
        name: this.packageForm.value.name!,
        description: this.packageForm.value.description ?? '',
        packageType: this.packageForm.value.packageType as any,
        active: this.packageForm.value.active ?? true,
      };
      this.dialogRef.close(packageData);
    }
  }
}

@Component({
  selector: 'app-admin-product-package',
  standalone: true,
  imports: [MaterialModule, FormsModule],
  templateUrl: './admin-product-package.component.html',
  styleUrl: './admin-product-package.component.scss'
})
export class AdminProductPackageComponent implements OnInit, OnDestroy {
  private _snackBar = inject(MatSnackBar);
  private cd = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  private productPackageService = inject(ProductPackageControllerService);
  private productService = inject(ProductControllerService);
  private dialog = inject(MatDialog);
  private productIdSub?: Subscription;

  @ViewChild(MatTable) table!: MatTable<ProductPackage>;

  productPackages: ProductPackage[] = [];
  products: Product[] = [];

  displayedColumns: string[] = [
    'id',
    'name',
    'packageType',
    'active',
    'items',
    'discounts',
    'createdAt',
    'actions',
  ];

  filterName: string = '';
  showOnlyActive: boolean = false;

  itemForm = this.fb.group({
    productId: [null as number | null, Validators.required],
    quantity: [1, [Validators.required, Validators.min(1)]],
    customPrice: [0, [Validators.min(0)]],
  });

  discountForm = this.fb.group({
    type: ['PERCENTAGE' as 'PERCENTAGE' | 'FIXED_AMOUNT', Validators.required],
    description: ['', Validators.required],
    percentage: [0],
    amount: [0],
    discountAmount: [0],
  });

  async ngOnInit() {
    await this.loadAllPackages();
    await this.loadProducts();

    this.productIdSub = this.itemForm.get('productId')!.valueChanges.subscribe((productId: number | null) => {
      if (!productId) return;
      const selected = this.products.find(p => p.id === productId);
      if (!selected) return;

      const currentPrice = this.itemForm.value.customPrice;
      if (currentPrice === null || currentPrice === undefined || currentPrice === 0) {
        this.itemForm.patchValue({ customPrice: selected.basePrice });
      }
    });
  }

  ngOnDestroy(): void {
    this.productIdSub?.unsubscribe();
  }

  async loadAllPackages() {
    try {
      const response = await firstValueFrom(
        this.productPackageService.findAll5(0, 100, 'id')
      );

      if (response && response.content) {
        this.productPackages = response.content;
      }

      this.cd.detectChanges();
      if (this.table) {
        this.table.renderRows();
      }
    } catch (error) {
      this._snackBar.open('Erro ao carregar pacotes', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro ao carregar pacotes:', error);
    }
  }

  async searchPackagesByName(name: string) {
    if (!name) return this.loadAllPackages();

    try {
      const response = await firstValueFrom(
        this.productPackageService.searchByNamePaginated(name, 0, 100, 'id')
      );

      if (response && response.content) {
        this.productPackages = response.content;
      }

      this.cd.detectChanges();
      if (this.table) this.table.renderRows();
    } catch (error) {
      this._snackBar.open('Erro ao buscar pacotes', 'Fechar', { duration: 5000 });
      console.error('Erro ao buscar pacotes:', error);
    }
  }

  async loadPackageById(id: number) {
    try {
      const packageData = await firstValueFrom(
        this.productPackageService.findById10(id)
      );
      const packageTotal = await this.calculatePackageTotal(id);

      this.openPackageDetailsDialog(packageData, packageTotal);
    } catch (error) {
      this._snackBar.open('Erro ao carregar pacote', 'Fechar', {
        duration: 5000,
      });
      console.error('Erro ao carregar pacote:', error);
    }
  }

  openPackageDetailsDialog(packageData: ProductPackage, packageTotal: number) {
    this.dialog.open(PackageDetailsDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      data: {
        package: packageData,
        packageTotal: packageTotal
      }
    });
  }

  openPackageDialog(pkg?: ProductPackage) {
    const dialogRef = this.dialog.open(AdminProductPackageDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: { package: pkg }
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.savePackage(result);
      }
    });
  }

  async savePackage(packageInput: ProductPackage) {
    try {
      if (packageInput.id) {
        await firstValueFrom(
          this.productPackageService.update9(packageInput.id, packageInput)
        );
        this._snackBar.open('Pacote atualizado com sucesso!', 'Fechar', { duration: 3000 });
      } else {
        await firstValueFrom(
          this.productPackageService.save9(packageInput)
        );
        this._snackBar.open('Pacote criado com sucesso!', 'Fechar', { duration: 3000 });
      }

      await this.loadAllPackages();
    } catch (error) {
      this._snackBar.open('Erro ao salvar pacote', 'Fechar', { duration: 5000 });
      console.error('Erro ao salvar pacote:', error);
    }
  }

  editPackage(pkg: ProductPackage) {
    this.openPackageDialog(pkg);
  }

  async deletePackage(id: number) {
    if (!confirm('Tem certeza que deseja excluir este pacote?')) return;

    try {
      await firstValueFrom(this.productPackageService.deleteById9(id));
      this._snackBar.open('Pacote excluído com sucesso!', 'Fechar', { duration: 3000 });
      await this.loadAllPackages();
    } catch (error) {
      this._snackBar.open('Erro ao excluir pacote', 'Fechar', { duration: 5000 });
      console.error('Erro ao excluir pacote:', error);
    }
  }

  async togglePackageStatus(pkg: ProductPackage) {
    if (!pkg.id) return;

    try {
      if (pkg.active) {
        await firstValueFrom(this.productPackageService.deactivate1(pkg.id));
        this._snackBar.open('Pacote desativado com sucesso!', 'Fechar', { duration: 3000 });
      } else {
        await firstValueFrom(this.productPackageService.activate1(pkg.id));
        this._snackBar.open('Pacote ativado com sucesso!', 'Fechar', { duration: 3000 });
      }
      await this.loadAllPackages();
    } catch (error) {
      this._snackBar.open('Erro ao alterar status do pacote', 'Fechar', { duration: 5000 });
      console.error('Erro ao alterar status do pacote:', error);
    }
  }

  async addItemToPackage(packageId: number) {
    if (this.itemForm.invalid) {
      this._snackBar.open('Por favor, preencha todos os campos do item', 'Fechar', { duration: 3000 });
      return;
    }

    try {
      const itemCreate: ProductPackageItem = {
        product: { id: this.itemForm.value.productId! } as any,
        quantity: this.itemForm.value.quantity!,
        unitPrice: this.itemForm.value.customPrice ?? undefined,
      };

      await firstValueFrom(this.productPackageService.addItem(packageId, itemCreate));

      this._snackBar.open('Item adicionado ao pacote com sucesso!', 'Fechar', { duration: 3000 });

      this.resetItemForm();
      await this.loadPackageById(packageId);
      await this.loadAllPackages();
    } catch (error) {
      this._snackBar.open('Erro ao adicionar item ao pacote', 'Fechar', { duration: 5000 });
      console.error('Erro:', error);
    }
  }

  async removeItemFromPackage(packageId: number, itemId: number) {
    if (!confirm('Tem certeza que deseja remover este item?')) return;

    try {
      await firstValueFrom(this.productPackageService.removeItem(packageId, itemId));

      this._snackBar.open('Item removido com sucesso!', 'Fechar', { duration: 3000 });

      await this.loadPackageById(packageId);
      await this.loadAllPackages();
    } catch (error) {
      this._snackBar.open('Erro ao remover item', 'Fechar', { duration: 5000 });
      console.error('Erro:', error);
    }
  }

  async addDiscountToPackage(packageId: number) {
    if (this.discountForm.invalid) {
      this._snackBar.open('Por favor, preencha todos os campos do desconto', 'Fechar',
        { duration: 3000 });
      return;
    }

    try {
      const discountType = this.discountForm.value.type!;
      let discountInput: ProductPackagePercentageDiscount | ProductPackageFixedAmountDiscount;

      switch (discountType) {
        case 'PERCENTAGE':
          if (!this.discountForm.value.percentage) {
            this._snackBar.open('Por favor, informe a porcentagem do desconto', 'Fechar', {
              duration: 3000,
            });
            return;
          }
          discountInput = {
            type: 'PERCENTAGE',
            description: this.discountForm.value.description!,
            percentage: this.discountForm.value.percentage,
            active: true,
          } as ProductPackagePercentageDiscount;
          break;

        case 'FIXED_AMOUNT':
          if (!this.discountForm.value.amount) {
            this._snackBar.open('Por favor, informe o valor do desconto', 'Fechar', {
              duration: 3000,
            });
            return;
          }
          discountInput = {
            type: 'FIXED',
            description: this.discountForm.value.description!,
            amount: this.discountForm.value.amount,
            active: true,
          } as ProductPackageFixedAmountDiscount;
          break;

        default:
          this._snackBar.open('Tipo de desconto inválido', 'Fechar', {
            duration: 3000,
          });
          return;
      }

      await firstValueFrom(this.productPackageService.addDiscount(packageId, discountInput));

      this._snackBar.open('Desconto adicionado ao pacote com sucesso!', 'Fechar', { duration: 3000 });

      this.resetDiscountForm();
      await this.loadPackageById(packageId);
      await this.loadAllPackages();
    } catch (error) {
      this._snackBar.open('Erro ao adicionar desconto ao pacote', 'Fechar', { duration: 5000 });
      console.error('Erro ao adicionar desconto ao pacote:', error);
    }
  }

  async removeDiscountFromPackage(packageId: number, discountId: number) {
    if (!confirm('Tem certeza que deseja remover este desconto?')) return;

    try {
      await firstValueFrom(this.productPackageService.removeDiscount(packageId, discountId));

      this._snackBar.open('Desconto removido com sucesso!', 'Fechar', { duration: 3000 });

      await this.loadPackageById(packageId);
      await this.loadAllPackages();
    } catch (error) {
      this._snackBar.open('Erro ao remover desconto', 'Fechar', { duration: 5000 });
      console.error('Erro ao remover desconto:', error);
    }
  }

  async calculatePackageTotal(packageId: number): Promise<number> {
    try {
      const total = await firstValueFrom(this.productPackageService.calculatePackageTotal(packageId));
      return total;
    } catch (error) {
      console.error('Erro ao calcular total do pacote:', error);
      return 0;
    }
  }

  async loadProducts() {
    try {
      this.products = await firstValueFrom(this.productService.findAllActive());
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    }
  }

  clearFilters() {
    this.filterName = '';
    this.showOnlyActive = false;
    this.loadAllPackages();
  }

  applyFilters() {
    if (this.filterName) {
      this.searchPackagesByName(this.filterName);
    } else {
      this.loadAllPackages();
    }
  }

  resetItemForm() {
    this.itemForm.reset({
      productId: null,
      quantity: 1,
      customPrice: 0,
    });
  }

  resetDiscountForm() {
    this.discountForm.reset({
      type: 'PERCENTAGE',
      description: '',
      percentage: 0,
      amount: 0,
    });
  }

  formatDate(date?: string): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('pt-BR');
  }

  formatPrice(price?: number): string {
    if (price === undefined || price === null) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  }

  getItemsCount(pkg: ProductPackage): number {
    return pkg.items?.length || 0;
  }

  getDiscountsCount(pkg: ProductPackage): number {
    return pkg.discounts?.length || 0;
  }

  getDiscountTypeLabel(type?: string): string {
    const typeMap: { [key: string]: string } = {
      'PERCENTAGE': 'Porcentagem',
      'FIXED_AMOUNT': 'Valor Fixo',
      'FIXED': 'Valor Fixo'
    };
    return type ? typeMap[type] || type : 'N/A';
  }

  getDiscountPercentage(discount: any): number | null {
    return discount && discount.percentage !== undefined ? discount.percentage : null;
  }

  getDiscountAmount(discount: any): number | null {
    return discount && discount.amount !== undefined ? discount.amount : null;
  }
}
